import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  Box,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Send as SendIcon,
  Lightbulb as LightIcon,
  Thermostat as ThermostatIcon,
  LocationOn as LocationIcon,
} from "@mui/icons-material";
import axios from "axios";
import LocationPermission from "./components/LocationPermission";
import WeatherWidget from "./components/WeatherWidget";
import LocationStartupNotification from "./components/LocationStartupNotification";
import ChatInterface from "./components/ChatInterface";
import locationService from "./services/locationService";
import weatherService from "./services/weatherService";
import timezoneService from "./services/timezoneService";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8080";

function App() {
  const [devices, setDevices] = useState([]);
  const [error, setError] = useState("");
  const [health, setHealth] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationPermission, setLocationPermission] = useState("unknown");
  const [weather, setWeather] = useState(null);
  const [quickCommandHandler, setQuickCommandHandler] = useState(null);

  // Initialize services
  useEffect(() => {
    const initializeServices = async () => {
      // Check API health
      try {
        const response = await axios.get(`${API_BASE_URL}/health`);
        setHealth(response.data);
      } catch (err) {
        setError(
          "Failed to connect to Arya backend. Make sure it's running on port 8080."
        );
      }

      // Initialize location, weather, and timezone services
      try {
        await locationService.initialize();
        await weatherService.initialize();
        // Timezone service initializes automatically
        console.log(
          "User timezone detected:",
          timezoneService.getUserTimezone()
        );

        // Auto-request location permission if conditions are met
        if (locationService.shouldAutoRequestLocation()) {
          // Small delay to let the UI render first
          setTimeout(async () => {
            try {
              const location =
                await locationService.requestLocationPermission();
              setCurrentLocation(location);
              setLocationPermission("granted");
            } catch (error) {
              console.log("Auto location request failed:", error.message);
              setLocationPermission("denied");
            }
          }, 1000);
        }
      } catch (err) {
        console.error("Failed to initialize services:", err);
      }
    };

    initializeServices();
  }, []);

  // Load devices
  const loadDevices = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/v1/devices`);
      setDevices(response.data.devices || []);
    } catch (err) {
      console.error("Failed to load devices:", err);
    }
  };

  useEffect(() => {
    loadDevices();
  }, []);

  // Control device directly
  const controlDevice = async (deviceId, action, params = {}) => {
    try {
      await axios.put(`${API_BASE_URL}/api/v1/devices/${deviceId}/control`, {
        action,
        params,
      });
      loadDevices(); // Reload to show changes
    } catch (err) {
      setError("Failed to control device");
    }
  };

  // Add sample device
  const addSampleDevice = async () => {
    try {
      await axios.post(`${API_BASE_URL}/api/v1/devices`, {
        name: "Sample Light",
        type: "light",
        location: "living_room",
        properties: {
          brightness: 50,
          power: false,
        },
      });
      loadDevices();
    } catch (err) {
      setError("Failed to add sample device");
    }
  };

  // Handle location updates
  const handleLocationUpdate = (location) => {
    setCurrentLocation(location);
  };

  // Handle location permission changes
  const handleLocationPermissionChange = (permission, location) => {
    setLocationPermission(permission);
    setCurrentLocation(location);
  };

  // Handle weather updates
  const handleWeatherUpdate = (weatherData) => {
    setWeather(weatherData);
  };

  // Handle location granted from startup notification
  const handleStartupLocationGranted = (location) => {
    setCurrentLocation(location);
    setLocationPermission("granted");
  };

  // Handle location denied from startup notification
  const handleStartupLocationDenied = (error) => {
    setLocationPermission("denied");
    console.log("Startup location request denied:", error.message);
  };

  const getDeviceIcon = (type) => {
    switch (type) {
      case "light":
        return <LightIcon />;
      case "thermostat":
        return <ThermostatIcon />;
      default:
        return <LightIcon />;
    }
  };

  return (
    <div className="App">
      {/* Location startup notification */}
      <LocationStartupNotification
        onLocationGranted={handleStartupLocationGranted}
        onLocationDenied={handleStartupLocationDenied}
      />

      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            🤖 Arya Personal Assistant
          </Typography>
          {health && (
            <Typography variant="body2">Status: {health.status}</Typography>
          )}
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Location Permission */}
          <Grid item xs={12} md={6}>
            <LocationPermission
              onLocationUpdate={handleLocationUpdate}
              onPermissionChange={handleLocationPermissionChange}
            />
          </Grid>

          {/* Weather Widget */}
          <Grid item xs={12} md={6}>
            <WeatherWidget
              location={currentLocation}
              onWeatherUpdate={handleWeatherUpdate}
            />
          </Grid>

          {/* Chat Interface */}
          <Grid item xs={12} md={8}>
            <ChatInterface
              currentLocation={currentLocation}
              locationService={locationService}
              onDeviceReload={loadDevices}
              onQuickCommand={setQuickCommandHandler}
            />
          </Grid>

          {/* Device Management */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6">🏠 Smart Devices</Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={addSampleDevice}
                  >
                    Add Sample Device
                  </Button>
                </Box>

                {devices.length === 0 ? (
                  <Typography color="text.secondary">
                    No devices found. Add a sample device to get started!
                  </Typography>
                ) : (
                  <List>
                    {devices.map((device) => (
                      <ListItem key={device.id} divider>
                        <ListItemText
                          primary={device.name}
                          secondary={`${device.type} • ${
                            device.location || "Unknown location"
                          }`}
                        />
                        <ListItemSecondaryAction>
                          {device.type === "light" && (
                            <Switch
                              checked={device.properties?.power || false}
                              onChange={(e) =>
                                controlDevice(
                                  device.id,
                                  e.target.checked ? "turn_on" : "turn_off"
                                )
                              }
                            />
                          )}
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Quick Commands */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  ⚡ Quick Commands
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Click any command to send it to Arya
                </Typography>
                <Grid container spacing={1}>
                  {[
                    "Turn on all lights",
                    "Turn off all lights",
                    "What devices are available?",
                    "Set living room light to 50% brightness",
                    "What's the weather like?",
                    "Give me a weather summary",
                    "What should I wear today?",
                    "Are there any weather alerts?",
                    "What's the forecast for this week?",
                  ].map((cmd) => (
                    <Grid item xs={12} key={cmd}>
                      <Button
                        variant="outlined"
                        size="small"
                        fullWidth
                        sx={{ justifyContent: "flex-start", textAlign: "left" }}
                        onClick={() => {
                          if (quickCommandHandler) {
                            quickCommandHandler(cmd);
                          }
                        }}
                      >
                        {cmd}
                      </Button>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </div>
  );
}

export default App;
